(ns ui.util
  (:require
   [cljs-uuid-utils.core :as cljs-uuid]
   [clojure.string :as string]
   [cognitect.transit :as t]
   [common.currencies :as currencies]
   [common.date-formats :as date-formats]
   ["clsx" :as clsx]
   ["tailwind-merge" :as tailwind-merge]
   ["html-to-text" :as html-to-text]
   ["date-fns" :as date-fns]
   ["date-fns/locale" :as date-fns-locale]
   [common.entity :as entity]
   [common.entity-utils :as eu]
   [common.localization :as loc]
   [common.number-format :as number-format]
   [common.schema.calculated-field :as calculated-field]
   [common.schema.registry :as registry]
   [common.timezones :as timezones]
   [common.util :as cu]
   [oops.core :as oops]
   [potpuri.core :as p]
   [ui.constants :as constants]
   [ui.dom-globals :as dg]
   [ui.tailwind-merge-tokens :as tw-merge-tokens]))

(defn vec-cat
  [& colls]
  (->> colls
       (apply concat)
       (vec)))

(defn error-response->message
  "Resolve a user-facing message from a backend error response following the
   string/loc-key/default convention: a string `:message` is used verbatim, a
   keyword `:error` is localized, otherwise a generic fallback."
  [{:keys [error message]}]
  (cond
    (string? message) message
    (keyword? error) (loc/tr error)
    :else (loc/tr :unknown-error-persists)))

(defn format-datetime
  [ts]
  (let [date (js/Date. ts)
        options #js {:year   "numeric"
                     :month  "numeric"
                     :day    "numeric"
                     :hour   "numeric"
                     :minute "numeric"}]
    (.toLocaleTimeString date js/undefined options)))

(defn format-date
  "TODO: This seems to be working, but needs more robust testing. "
  [date-utc-ms date-format]
  (when-not (cu/empty-value? date-utc-ms)
    (let [timezone-offset (dg/date->timezone-offset (js/Date. date-utc-ms))
          date-ts (+ date-utc-ms (* timezone-offset 60000))
          value-date (js/Date. date-ts)]
      (try
        (date-fns/format value-date date-format #js {:locale date-fns-locale/enUs})
        (catch js/Object _err
          (js/console.error "Failed to format date : " date-utc-ms " << " date-format)
          date-utc-ms)))))

(defn parse-int
  [s]
  (js/parseInt s))

(defn parse-float-input
  [decimal-separator input]
  (let [escaped-decimal-sep (if (= decimal-separator ".")
                              "\\."
                              decimal-separator)
        matcher (re-pattern (str "[^-0-9" escaped-decimal-sep "]"))
        filtered-input (string/replace input matcher "")
        float-pattern (re-pattern (case decimal-separator
                                    "" "^-?(\\d*)"                                                  ; when no decimal separator, avoid regex performance problem
                                    (str "^-?(\\d*" escaped-decimal-sep "?)\\d*")))
        cleaned-input (apply str
                             (->> (re-seq float-pattern filtered-input)
                                  first
                                  first))                                                           ;; take the first grouping
        js-format (string/replace cleaned-input decimal-separator ".")                              ;; . is js decimal separator
        ]
    js-format))

(defn html->text
  [html-str]
  (let [text (oops/ocall! html-to-text "convert" html-str (clj->js {:wordwrap 130}))]
    text))

(defn parse-float-info
  ([decimal-separator input]
   (parse-float-info decimal-separator input 0))
  ([decimal-separator input NaN]
   (let [input (or input "")
         js-format (parse-float-input decimal-separator input)
         float-value (js/parseFloat js-format)
         decimal-place (.indexOf js-format ".")                                                     ;; . is js decimal separator
         decimal-count (if (< decimal-place 0)
                         -1
                         (count (.substring js-format (+ 1 decimal-place))))
         negative? (= (.indexOf js-format "-") 0)
         lcase-last (->> input
                         (last)
                         (str)
                         (string/trim)
                         (string/lower-case))
         multiplier (case lcase-last
                      "m" 1000000
                      "k" 1000
                      1)]
     {:float-value   (if (js/isNaN float-value)
                       NaN
                       (float (* multiplier float-value)))
      :string-value  js-format
      :decimal-count decimal-count
      :negative?     negative?})))                                                                  ;-1 = no separator, 0 = separator but no digits, 1+ decimal count

(defn format-with-separators
  ([number]
   (format-with-separators nil number -1 false 4 false))
  ([number-format number min-decimals]
   (format-with-separators number-format number min-decimals false 4 false))
  ([number-format number min-decimals force-negative?]
   (format-with-separators number-format number min-decimals force-negative? 4 false))
  ([number-format number min-decimals force-negative? max-decimals]
   (format-with-separators number-format number min-decimals force-negative? max-decimals false))
  ([number-format number min-decimals force-negative? max-decimals read-mode?]
   (let [parts (string/split (str number) ".")                                                      ;; javascript native separator is '.'
         [thousand-separator decimal-separator] (if (nil? number-format)
                                                  [constants/thousand-separator constants/decimal-separator]
                                                  (number-format/separators-as-strings number-format))
         integer-part
         (if (> (count thousand-separator) 0)
           (string/replace (first parts) #"\B(?=(\d{3})+(?!\d))" thousand-separator)
           (first parts))
         dec-part (second parts)
         #_scientific-notation? #_(when dec-part
                                    (or (string/includes? dec-part "e")
                                        (string/includes? dec-part "E")))
         decimal-part (cond
                        (> min-decimals 0)
                        (str decimal-separator
                             dec-part
                             (subs "000000000000000" 0 (max (- min-decimals (count dec-part)) 0)))  ; pad with trailing 0's
                        dec-part
                        (str decimal-separator dec-part)
                        (zero? min-decimals)
                        (if read-mode?
                          ""
                          (str decimal-separator)))]
     (str (when (and force-negative? (not= (.indexOf integer-part "-") 0))
            "-")
          (if (js/isNaN (first parts))
            0
            integer-part)
          (cond (= max-decimals 0)
                ""
                (and decimal-part
                     (> (count (str dec-part)) (or max-decimals 4)))
                (subs decimal-part 0 (or max-decimals 4))
                :else
                decimal-part)))))

(defn round
  [val decimals]
  (let [multiplier (reduce * (repeat decimals 10))]
    (-> val
        (+ js/Number.EPSILON)                                                                       ;; ensure no rounding errors due to floating point
        (* multiplier)
        (js/Math.round)
        (/ multiplier))))

(defn- format-val
  [number-format value divider postfix]
  (let [default-currency-decimal-places 2
        formatted-number (format-with-separators number-format
                                                 (round (/ value divider)
                                                        default-currency-decimal-places)
                                                 default-currency-decimal-places
                                                 false
                                                 default-currency-decimal-places
                                                 true)]
    (str formatted-number postfix)))

(defn multiplier-str->divider
  [multiplier-str]
  (case (string/lower-case (or multiplier-str ""))
    nil 1
    "" 1
    "1" 1
    "m" 1000000
    "lacs" 100000
    "cr" 10000000
    "k" 1000
    (do (js/console.log (str "Unknown multiplier ['" multiplier-str "']"))
        1)))

(defn format-number-with-mult
  ([value multiplier-arg]
   (format-number-with-mult nil value multiplier-arg))
  ([number-format value _multiplier-arg]
   (let [multiplier "1"]
     (if (or (nil? value)
             (= value "")
             (and (string? value) (js/isNaN value)))
       ""
       (case multiplier
         nil (format-val number-format value 1 "")
         "" (format-val number-format value 1 "")
         "1" (format-val number-format value 1 "")
         "m" (format-val number-format value 1000000 "M")
         "M" (format-val number-format value 1000000 "M")
         "lacs" (format-val number-format value 100000 "Lacs")
         "Cr" (format-val number-format value 10000000 "Cr")
         "k" (format-val number-format value 1000 "k")
         "K" (format-val number-format value 1000 "k")
         (do (js/console.log (str "Unknown multiplier ['" multiplier "']"))
             (format-val number-format value 1 "")))))))

(defn is-bigdec?
  "true if bigdec"
  [b]
  (t/bigdec? b))

(defn bigdec->float
  "Converts bigdec to numeric values, might lose some precision as js does not support bigdec.
   However, for currency, this case should not arise"
  [x]
  (let [w (t/writer :json)
        r (t/reader :json
                    {:handlers {"f" (fn [x] (js/parseFloat x))}})]
    (when x
      (t/read r (t/write w x)))))

(defn float->bigdec
  [f]
  (-> f
      str
      t/bigdec))

(defn make-uuid
  ([]
   (make-uuid ""))
  ([prefix]
   (let [uuid (->> (cljs-uuid/make-random-uuid)
                   (cljs-uuid/uuid-string))]
     (str prefix uuid))))

(def tw-merge-fn (aget tailwind-merge "twMerge"))
(def clsx-fn (aget clsx "clsx"))

(defn cm-legacy
  "classname-merge.

   Combines tailwind CSS class names using `clsx` and merges them using `twMerge`.

  This function takes a variable number of class name inputs, combines them using
  the `clsx` utility, and then merges the resulting class names using the
  `twMerge` utility from Tailwind CSS. It is useful for conditionally applying
  and merging Tailwind CSS class names.

   Copied from shadcn/ui"
  [& inputs]
  (let [inputs (clj->js inputs)
        clsx-res (.apply clsx-fn nil inputs)
        tw-merge-res (.apply tw-merge-fn nil (array clsx-res))]
    tw-merge-res))

(def extend-tw-merge-fn (aget tailwind-merge "extendTailwindMerge"))

(def tw2-font-size-tokens
  "`text-*` names that set the font size but that tailwind-merge cannot tell
   apart from a color: the semantic typography composites in
   src/css/tw2/components.css, plus the non-t-shirt `fontSize` keys in
   tailwind.tw2.upstream.config.js. Unregistered, they fall into `text-color`,
   and a later `tw2-text-primary` in the same `cm` call silently drops
   `tw2-text-heading-sm`. Extend this when upstream adds a composite."
  ["label-sm" "label-md"
   "body-sm" "body-md"
   "heading-sm" "heading-md" "heading-lg"
   "eyebrow" "helper"
   "modal-title"])

(def tw2-merge-fn
  "twMerge variant that recognizes tw2-prefixed classes as belonging to the
   same class groups as their unprefixed counterparts, so e.g. `tw2-p-4` and
   `p-4` merge/override each other correctly. CLJS port of:

     extendTailwindMerge((config) => {
       config.classGroups['font-size'].push({ text: tw2FontSizeTokens })
       addSizeTokens(config.classGroups)
       Object.keys(config.classGroups).forEach((key) => {
         const value = config.classGroups[key]
         config.classGroups[key] = [...value, { tw2: value }]
       })
       return config
     })

   The font-size tokens are registered first so the tw2 mirroring picks them
   up too. Exact names take precedence over the `text-color` catch-all, so
   colors keep merging as colors.

   `addSizeTokens` is `tw-merge-tokens/add-size-tokens!`. It registers the
   custom width and height keys, so `tw2-w-card` and `tw2-w-full` override
   each other. It runs before the mirroring for the same reason."
  (extend-tw-merge-fn
   (fn [config]
     (let [class-groups (aget config "classGroups")]
       (.push (aget class-groups "font-size")
              (js-obj "text" (clj->js tw2-font-size-tokens)))
       (tw-merge-tokens/add-size-tokens! class-groups)
       (doseq [k (js-keys class-groups)]
         (let [value (aget class-groups k)]
           (aset class-groups k
                 (.concat value #js [(js-obj "tw2" value)])))))
     config)))

(defn cm
  "classname-merge for a codebase mixing tw2-prefixed and non-prefixed Tailwind
   classes. Same shape as `cm-legacy` — variadic inputs → clsx → tailwind-merge —
   but the underlying merger is extended so tw2- variants live in the same
   class groups as their bare counterparts."
  [& inputs]
  (let [inputs (clj->js inputs)
        clsx-res (.apply clsx-fn nil inputs)
        tw-merge-res (.apply tw2-merge-fn nil (array clsx-res))]
    tw-merge-res))

(defn display-date-timestamp
  [tenant value-ts]
  (some-> value-ts
          (date-formats/format-millis
           (or (:tenant/date-format tenant)
               (loc/tr :date-format))
           (or
            nil
            ;; DATE is stored in UTC 00:00 -> DO NOT USE TIMEZONE when displaying
            ;(:tenant/timezone @session/tenant)
            timezones/default-timezone))))

;; tenant (or default) format, timezone has no effect.
(defn display-datetime-timestamp
  [tenant value-ts]
  (some-> value-ts
          (date-formats/format-millis
           (str (or (:tenant/date-format tenant) (loc/tr :date-format))
                " hh:mm:ss")
           (or (:tenant/timezone tenant)
               timezones/default-timezone))))

(defn find-option-label
  [options value-kw]
  (let [element (when (keyword? value-kw)
                  (or (p/find-first options {:keyword value-kw})
                      (p/find-first options {:key value-kw})
                      (p/find-first options {:value value-kw})))]
    (:label element)))

(defn display-entity
  [entity-id id->entity]
  (let [entity (get id->entity (or (:db/id entity-id) entity-id))
        display-str (if entity
                      (entity/display-string entity :short id->entity)
                      (js/console.log "Cannot resolve display str for " entity-id " < " (type entity-id)))]
    display-str))

(defn display-entities
  [entity-ids id->entity]
  (->> entity-ids
       (keep (fn [eid]
               (get id->entity eid)))
       (keep (fn [entity]
               (when-let [display-str (entity/display-string entity :short id->entity)]
                 {:value (:db/id entity)
                  :label display-str})))
       (sort-by (fn [{:keys [label]}]
                  (string/lower-case label)))
       (vec)))

(defn field-value->materialized-value
  "TODO this is still in progress, only for proof of concepts.
   Do not use in any production features"
  [{:keys [field value id->entity tenant tenant-schema
           options entity show-currency? skip-multiplier? preserve-rich-text?]}]
  (let [field-schema (registry/get-field-schema tenant-schema field)
        options (or options (:options field-schema))
        data-type (-> field-schema
                      :field/data-type)
        named-value? (-> field-schema
                         :named-value-attribute)
        entities id->entity
        calc-function (:field/calculated-field field-schema)]
    #_(js/console.log "Materialized : " field " > " data-type)
    (cond
      (fn? calc-function)
      (let [calculated-value
            (calculated-field/calculate
             {:calc-fn          calc-function
              :entity-data      (if (or (not (coll? field))
                                        (= (count field) 1))
                                  entity
                                  ;; FIXME: nested field values
                                  nil)
              :entity-lookup    (fn [eid]
                                  (let [id (or (:db/id eid)
                                               eid)]
                                    (get entities id)))
              ;; FIXME
              :reverse-lookup   (fn []
                                  nil)
              :tr               loc/tr
              :bigdec->float    bigdec->float
              :resolve-value    (partial eu/get-value tenant tenant-schema)
              :float->bigdec    float->bigdec
              :get-field-schema (partial registry/get-field-schema tenant-schema)
              :display-string   entity/display-string})]
        ;; FIXME: Recursively resolve the materialized value
        (cond
          (= data-type :data.type/ref-multi)
          (display-entities calculated-value id->entity)

          :else
          calculated-value))

      (nil? value)
      nil

      ;;specific handling of :oppo/ratings:
      (= data-type :data.type/percentage)
      (str (some-> value
                   (* 100))
           (when value "%"))

      (= data-type :data.type/number)
      value

      (= data-type :data.type/text-richtext)
      (if preserve-rich-text?
        value
        (some-> value
                html->text))

      (string? value)
      value

      (and (= field :entity/type) (keyword? value))
      (loc/tr value)

      (and (= data-type :data.type/keyword)
           (seq options))
      (find-option-label options value)

      (and (= data-type :data.type/keyword-multi)
           (seq options))
      (some->> value
               (mapv (fn [value-kw]
                       {:value value-kw
                        :label (find-option-label options value-kw)})))

      (= data-type :data.type/datetime)
      (display-datetime-timestamp tenant value)

      (= data-type :data.type/date)
      (display-date-timestamp tenant value)

      (= data-type :data.type/ref-single)
      (display-entity value id->entity)

      (and (= data-type :data.type/ref-multi)
           named-value?)
      (display-entities value id->entity)

      (= data-type :data.type/ref-multi)
      (display-entities value id->entity)

      ;; Todo add formatting
      (= data-type :data.type/currency-value)
      (let [default-currency-decimal-places
            (get tenant :tenant/default-currency-decimal-places 2)

            cur-val (if skip-multiplier?
                      (some-> value
                              (bigdec->float)
                              (round default-currency-decimal-places)
                              (format-with-separators default-currency-decimal-places
                                                      false
                                                      default-currency-decimal-places))
                      (some-> value
                              (bigdec->float)
                              (format-number-with-mult nil)))

            cur-attr (:field/secondary-attribute field-schema)
            cur-sym (or (get entity cur-attr)
                        (:tenant/default-currency tenant))]
        (if (and show-currency? cur-sym)
          (str (currencies/get-label cur-sym)
               " "
               cur-val)
          cur-val))

      (= data-type :data.type/currency-symbol)
      (when value
        (currencies/get-label value))

      ;; FIXME:
      (= data-type :data.type/multiplier)
      (str value (when value "x"))

      (= data-type :data.type/boolean)
      (if value
        (loc/tr :true)
        (loc/tr :false))

      ;; TODO: Add all of these special cases somewhere
      (= field :common/named-network-drives)
      (some->> value
               (mapv (fn [{:keys [url label]}]
                       {:value url
                        :label label})))

      :else value)))

(defn entity->materialized-entity
  [{:keys [entity id->entity tenant tenant-schema]}]
  (->> entity
       (map (fn [[field value]]
              (let [materialized-value (field-value->materialized-value
                                        {:field         field
                                         :value         value
                                         :id->entity    id->entity
                                         :tenant        tenant
                                         :tenant-schema tenant-schema})]
                [field materialized-value])))
       (into {})))

(defn timeout
  [num f]
  (js/setTimeout f num))

(defn evt-target-value
  [e]
  (-> e .-target .-value))

(defn list->and-string
  [items]
  (cond (zero? (count items))
        ""
        (= 1 (count items))
        (first items)
        :else
        (loc/tr :and [(->> items
                           (butlast)
                           (string/join ", "))
                      (last items)])))

(defn with-react-key
  "Attach a stable, client-only React key to `x` as metadata under :temp-react-key.
   For list elements built from data that has no unique id but supports reorder:
   the key survives assoc/update and reordering, is ignored by clojure.data/diff, and is
   dropped by transit serialization, so it never reaches the backend and never reads as a
   change. Rebuilding a value via (into {} ...)/merge/select-keys/etc. drops the metadata —
   re-apply there (e.g. (with-meta (merge a b) (meta b))) when it must be preserved."
  [x]
  (with-meta x {:temp-react-key (str (random-uuid))}))

(defn react-key
  "Read the client-only React key attached by `with-react-key`, or nil if absent.
   Use directly as `^{:key (uu/react-key item)}` with no index fallback, so a nil key
   surfaces React's missing-key warning wherever the metadata got lost."
  [x]
  (-> x meta :temp-react-key))
